/* t7-2b Bloque B (#518) -- EL CAJON "PREGUNTALE AL PANEL" (diseno seccion 3.3, pantalla 1c).

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos): todo texto que se ve
   sale de `dashboard:groups.avance.chat.*` (y dos reusados: el titulo de la pastilla,
   `groups.avance.preguntale`, y `common:close`).

   QUIEN LO MONTA: la pagina "Avance del grupo" (`pages/GroupAvancePage.js`), solo cuando el
   servidor dice `chat_disponible: true`; sus tres pastillas lo abren. Le pasa el nombre del
   grupo, la hora de los datos ("Datos de hoy") y el primer alumno de "Quien necesita ayuda"
   (el chip "Como va ..."). Va en un PORTAL al `body`: la pagina vive dentro de un contenedor
   animado con transform, y una capa fija ahi dentro quedaria atada a la pagina.

   LA GEOMETRIA (medidas del diseno en `style`, como la pagina del avance): a la derecha, 440 px,
   fondo 121212, borde izquierdo 2a2a2a y sombra a la izquierda, sobre la pagina atenuada; en el
   telefono (debajo de 768 px de ventana, el corte `md` de la casa, `useEsAncho`) ocupa toda la
   pantalla. Las zonas seguras van en clases con el respaldo de la casa (`env(..., 0px)`): la de
   arriba en la cabecera, la de abajo en el pie, la derecha siempre y la izquierda solo en el
   telefono (en la computadora el cajon no toca el borde izquierdo de la pantalla).

   LO QUE HACE (todas las llamadas por `lib/chatDelPanel.js`):
     - al abrir pide la lista de chats del grupo y abre el mas reciente; "Nuevo chat" crea uno
       vacio; el selector cambia de chat; "Borrar chat" pide confirmacion en una ventana chica
       de la casa (las mismas clases que las de `GroupBankTemasPage.js`);
     - mandar: la pregunta y sus archivos en un FormData; si no hay ningun chat, primero crea
       uno. Mientras contesta, la pregunta ya se ve y abajo "Pensando..." con el chasis de
       espera de la casa (`SkeletonBlock`). Si falla, el texto del error por su CODIGO, y la
       pregunta y sus archivos vuelven a la caja (el servidor no guardo nada);
     - los adjuntos se validan en el cliente ANTES de mandar (hasta 3, hasta 5 MB, los tipos
       que el servidor lee);
     - los mensajes del panel se pintan por BLOQUES; cada sugerencia va aparte con su rotulo, y
       las marcas jamas se ven crudas (si un mensaje llega sin bloques, se parte aqui con la
       misma regla). Bajo la respuesta, "Lei las primeras n paginas" por cada adjunto recortado
       de la pregunta.
   Accesible: `role="dialog"` con `aria-modal`, el foco a la caja al abrir y de vuelta a la
   pastilla al cerrar, Escape cierra (o, con la ventana chica abierta, solo la ventana chica).

   t7-3 remate 8 (#590 y #591):
     - LAS SUGERENCIAS salen de `sugerenciasDelCajon` (con el avance que el panel ya tiene: hasta
       cuatro, en su orden) y son mas compactas (letra de 12, relleno de 10; el dibujo de 32 y el
       toque de 44 se conservan). Con el chat vacio estan a la vista; en cuanto hay un mensaje (o
       una pregunta en camino) se pliegan detras de "Sugerencias", que se abre con su flecha; al
       elegir una se manda y se vuelven a plegar.
     - EL FOCO no se sale del cajon ni de su ventana chica (Tab y Mayus+Tab dan la vuelta; C28,
       C91 y C131); despues de borrar un chat va al selector (o a "Nuevo chat"), y despues de
       quitar un adjunto, al siguiente (o a la caja).
     - ATRAS (del navegador o del telefono) cierra el cajon en vez de salir del panel, y lo
       escrito se queda (C34).
     - LA CAJA crece al escribir hasta 4 renglones y despues se desliza por dentro (C98).
     - LO LEIDO de un adjunto recortado se dice segun su archivo: paginas, diapositivas o "la
       primera parte del documento" (C12).

   post-t7-2b (C3, #607): LA LISTA DE CHATS, portada tal cual de la maqueta aprobada (pestana "Lista de chats"). El
   selector del sistema se fue (en Windows salia blanco y azul y cortaba los nombres): el boton (el mismo testid y el
   mismo rotulo) abre una lista PROPIA dentro del cajon, debajo de el y a todo el ancho del renglon; cada chat con su
   fecha arriba y chica y su nombre entero hasta dos renglones; el abierto en naranja suave con palomita; 44 de alto
   por renglon; 16 px de letra en aparatos tactiles; flechas, Enter, Esc que cierra la lista SIN cerrar el cajon (el
   foco vuelve al boton) y un clic fuera que la cierra. No es un portal de Radix: el cajon ya vive en un portal con
   trampa de foco, y los renglones (tabindex -1) no entran al recorrido de Tab. */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, MessageCircle, Paperclip, Plus, Send, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { SkeletonBlock } from './skeletons';
import { useEsAncho, useMedia } from '../lib/useEsAncho';
import {
  ACEPTA, ALTO_DE_LA_CAJA, TOPE_PREGUNTA, agregarAdjuntos, altoDeLaCaja, bloquesDelMensaje, borrarChat, crearChat,
  errorDelServidor, errorDeAccion, etiquetaDelChat, leerChat, listarChats, llaveDelCliente, loLeido, mandarPregunta,
  ordenarChats, revisarAdjuntos, sugerenciasDelCajon,
} from '../lib/chatDelPanel';

/** t7-3 remate 8 (C28, C91 y C131): lo que se puede enfocar dentro del cajon o de su ventana chica. */
const ENFOCABLES = 'button:not([disabled]), select:not([disabled]), textarea:not([disabled]), '
  + 'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), a[href], [tabindex]:not([tabindex="-1"])';

function enfocables(raiz) {
  if (!raiz) return [];
  return Array.from(raiz.querySelectorAll(ENFOCABLES)).filter((el) => el.getAttribute('aria-hidden') !== 'true');
}

const NARANJA = '#FF6B00';
const GRIS = '#a0a0a0';
const ANCHO_DEL_CAJON = 440;
/* Con poco alto (un telefono apaisado, una ventana baja) la cabecera y el pie no caben con la
   conversacion: esta no baja de este alto y el cajon entero se desliza (medido en Chrome a
   844 x 390: sin esto la conversacion quedaba en 32 px y el pie se salia por abajo). */
const ALTO_MINIMO_DE_LA_CONVERSACION = 160;
const L = 'groups.avance.chat';

/* t7-2b remate (R3a): EL AREA TOCABLE DE 44 SIN CAMBIAR EL DIBUJO. "Nuevo chat", los chips de
   preguntas, "Cerrar" y "Borrar chat" miden 32 de alto (la letra del diseno) y la regla de la
   casa para tocar es 44. Es el patron del verbo "Apagar / Encender" de `GroupAvanceRecuadro.js`
   (el area crece hacia afuera y el dibujo no se mueve), pero aqui no puede ir como relleno
   del boton: estos llevan borde y fondo, y el relleno agrandaria el dibujo. Por eso crece con
   una EXTENSION: un hijo transparente, absoluto y vacio que sale 6 px por arriba y 6 por
   abajo de la caja del boton (32 + 6 + 6 = 44); en "Borrar" (32 x 32) tambien 6 por cada
   lado. Un hijo absoluto se mide desde DENTRO del borde, asi que en los botones con borde de
   1 px sale 1 px mas (medido en Chrome: sin eso quedaban en 42). Tocarla es tocar el boton
   (vive dentro de el). No ocupa lugar, asi que nada se mueve. Va en todos los anchos: en un
   puntero fino solo agranda un poco donde se enciende el boton. */
const ALTO_DEL_DIBUJO = 32;
const ALTO_TOCABLE = 44;
const EXTRA_TOCABLE = (ALTO_TOCABLE - ALTO_DEL_DIBUJO) / 2;
/* t7-3 remate 8 (#590): el vestido de un chip de sugerencia, mas compacto (letra de 12 y relleno lateral de 10; antes
   13 y 12). El dibujo de 32 y el toque de 44 se conservan. */
const ESTILO_DEL_CHIP = {
  position: 'relative', minHeight: ALTO_DEL_DIBUJO, paddingLeft: 10, paddingRight: 10, paddingTop: 6,
  paddingBottom: 6, lineHeight: '18px', borderRadius: 999, borderWidth: 1, borderStyle: 'solid',
  borderColor: '#333333', fontSize: 12, whiteSpace: 'normal', maxWidth: '100%', textAlign: 'left',
  flexShrink: 1,
};

function AreaTocable({ borde = 0, deLado = false }) {
  const fuera = -(EXTRA_TOCABLE + borde);
  return (
    <span aria-hidden="true" data-area-tocable="si"
      style={{
        position: 'absolute', top: fuera, bottom: fuera,
        left: deLado ? fuera : -borde, right: deLado ? fuera : -borde,
      }} />
  );
}

/* La ventana chica de la casa: capa, panel, titulo y botonera con las MISMAS clases que las
   ventanas chicas de la pagina de temas del profe (ahi viven como VENTANA_PANEL, VENTANA_TITULO,
   BOTONERA, BOTON_CANCELAR y BOTON_GUARDAR). La capa va en `style` porque tiene que quedar
   encima del cajon. */
const VENTANA_PANEL = 'relative bg-[#111] border border-[#262626] rounded-[20px] '
  + 'p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] max-w-md '
  + 'w-full mx-4 shadow-2xl max-h-full overflow-y-auto';
const VENTANA_TITULO = 'text-lg font-semibold text-white mb-3';
const BOTONERA = 'flex flex-col sm:flex-row gap-3';
const BOTON_CANCELAR = 'w-full sm:w-auto sm:flex-1 border-[#333] text-[#a0a0a0] '
  + 'rounded-xl min-h-[44px] text-sm';
const BOTON_GUARDAR = 'w-full sm:w-auto sm:flex-1 bg-[#FF6B00] hover:bg-[#E05A00] '
  + 'text-white rounded-xl min-h-[44px] text-sm disabled:opacity-40';

const BOTON_NARANJA = 'bg-[#FF6B00] hover:bg-[#E05A00] text-white disabled:opacity-40';

function idiomaDe(i18n) {
  return String((i18n && i18n.language) || 'es').toLowerCase().startsWith('en') ? 'en' : 'es';
}

/* -- los mensajes --------------------------------------------------------------- */

function MensajeDelProfe({ t, mensaje, testid }) {
  return (
    <div data-testid={testid} data-rol="profe" style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <div data-testid="chat-globo"
        style={{
          maxWidth: '85%', backgroundColor: NARANJA, color: '#ffffff', borderRadius: '14px 14px 4px 14px',
          paddingTop: 10, paddingBottom: 10, paddingLeft: 14, paddingRight: 14,
          fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word',
        }}>
        {mensaje.texto}
        {(mensaje.adjuntos || []).map((a, k) => (
          <p key={k} data-testid="chat-adjunto-linea" style={{ fontSize: 12, marginTop: 6, opacity: 0.9 }}>
            {t(`${L}.archivo_adjunto`, { nombre: a.nombre, name: a.nombre })}
          </p>
        ))}
      </div>
    </div>
  );
}

const GLOBO_DEL_PANEL = {
  backgroundColor: '#1c1c1c', borderWidth: 1, borderStyle: 'solid', borderColor: '#2a2a2a', borderRadius: 14,
  paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14,
  color: '#e5e5e5', fontSize: 14, lineHeight: 1.5, overflowWrap: 'anywhere', wordBreak: 'break-word',
};

function MensajeDelPanel({ t, mensaje, pregunta, testid }) {
  const bloques = bloquesDelMensaje(mensaje);
  const leidas = loLeido(pregunta);
  return (
    <div data-testid={testid} data-rol="panel" style={{ display: 'flex', justifyContent: 'flex-start' }}>
      <div style={{ maxWidth: '92%', minWidth: 0 }}>
        <div data-testid="chat-globo" style={GLOBO_DEL_PANEL}>
          {bloques.map((b, k) => (b.tipo === 'sugerencia' ? (
            <div key={k} data-testid="chat-bloque" data-tipo="sugerencia"
              style={{
                marginTop: k ? 10 : 0, paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12,
                borderRadius: 10, backgroundColor: '#161616', borderWidth: 1, borderStyle: 'solid', borderColor: '#2e2e2e',
              }}>
              <p data-testid="chat-sugerencia-rotulo"
                style={{ fontSize: 11, color: GRIS, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4 }}>
                {t(`${L}.sugerencia_rotulo`)}
              </p>
              <p style={{ whiteSpace: 'pre-wrap' }}>{b.texto}</p>
            </div>
          ) : (
            <p key={k} data-testid="chat-bloque" data-tipo="dato" style={{ marginTop: k ? 10 : 0, whiteSpace: 'pre-wrap' }}>
              {b.texto}
            </p>
          )))}
        </div>
        {leidas.map((x, k) => (
          <p key={k} data-testid="chat-leidas" style={{ fontSize: 12, color: GRIS, marginTop: 6 }}>
            {x.n === null ? t(`${L}.${x.llave}`) : t(`${L}.${x.llave}`, { count: x.n, n: x.n })}
          </p>
        ))}
      </div>
    </div>
  );
}

function Pensando({ t }) {
  return (
    <div data-testid="chat-pensando" role="status" aria-live="polite" style={{ display: 'flex', justifyContent: 'flex-start' }}>
      <div style={{ ...GLOBO_DEL_PANEL, width: '70%' }}>
        <p style={{ fontSize: 13, color: GRIS }}>{t(`${L}.pensando`)}</p>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <SkeletonBlock className="h-3 w-full" />
          <SkeletonBlock className="h-3 max-w-[60%]" />
        </div>
      </div>
    </div>
  );
}

/* ======================================================================
   EL CAJON
   ====================================================================== */

// LAPIDA post-t7-2b (C3, #607): aqui vivia `TOQUE_DEL_SELECTOR`, la etiqueta que le daba 44 de toque al selector del
// sistema (t7-3 remate 7, pasada 4, regla 2). El boton de la lista lleva su propia extension (`AreaTocable`), como
// "Nuevo chat" y "Borrar chat".

/* post-t7-2b (C3, #607): LA LISTA DE CHATS, con las medidas y los colores de la maqueta aprobada (los de la casa). */
const ACENTO_SUAVE = 'rgba(255, 107, 0, 0.12)';
const NARANJA_CLARO = '#ffb27a';
const FONDO_RESALTADO = '#222222';
// la consulta de la maqueta para la letra de 16 px en lo que se toca (cero zoom en el iPhone)
const CONSULTA_TACTIL = '(hover: none) and (pointer: coarse)';

/** La fecha y el nombre de un chat por separado, con el MISMO formateador de siempre (`etiquetaDelChat`): su texto es
    "fecha: nombre", o solo el nombre si no hay fecha legible. */
function partesDelChat(t, chat, lang) {
  const titulo = String((chat && chat.titulo) || '').trim() || t(`${L}.nuevo`);
  const etiqueta = etiquetaDelChat(t, chat, lang);
  const cola = `: ${titulo}`;
  const cuando = etiqueta !== titulo && etiqueta.endsWith(cola) ? etiqueta.slice(0, -cola.length) : '';
  return { cuando, titulo };
}

export default function PanelChatCajon({ abierto, alCerrar, groupId, grupoNombre, hora, avance, volverA }) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const lang = idiomaDe(i18n);
  const ancho = useEsAncho();
  const telefono = !ancho;
  const tactil = useMedia(CONSULTA_TACTIL, false);

  const [chats, setChats] = useState(null); // null: todavia no llega la lista
  const [actual, setActual] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [pendiente, setPendiente] = useState(null); // la pregunta en vuelo
  const [error, setError] = useState(null); // {llave, params}
  const [confirmando, setConfirmando] = useState(false);
  const boteRef = useRef(null);
  const volverAlBoteRef = useRef(false);
  const [ocupado, setOcupado] = useState(false);
  // t7-3 remate 8 (#590): las sugerencias plegadas detras de "Sugerencias" cuando ya hay conversacion
  const [sugerenciasAbiertas, setSugerenciasAbiertas] = useState(false);
  const enfocarRef = useRef(null); // a donde va el foco despues de pintar: 'chip', 'plegar', {quitar: i}
  const cajonRef = useRef(null);
  const confirmarRef = useRef(null);
  const selectorRef = useRef(null);
  const nuevoRef = useRef(null);
  const plegarRef = useRef(null);
  const chipsRef = useRef(null);
  const archivosRef = useRef(null);

  const actualRef = useRef(null);
  const pedidoRef = useRef(0);
  const cajaRef = useRef(null);
  const listaRef = useRef(null);
  const archivoRef = useRef(null);
  const cancelarRef = useRef(null);
  const estabaAbierto = useRef(abierto);
  // post-t7-2b (C3, #607): la lista de chats abierta, el renglon resaltado (raton o foco) y su caja
  const [listaAbierta, setListaAbierta] = useState(false);
  const [resaltado, setResaltado] = useState(null);
  const listaAbiertaRef = useRef(false);
  const cajaDeLaListaRef = useRef(null);
  const listaDeChatsRef = useRef(null);
  listaAbiertaRef.current = listaAbierta;

  const elegir = (id) => {
    actualRef.current = id;
    setActual(id);
  };

  // Pide un chat y pinta sus mensajes; si mientras tanto se eligio otro, no pisa nada.
  const cargarChat = useCallback(async (id) => {
    const turno = pedidoRef.current + 1;
    pedidoRef.current = turno;
    try {
      const chat = await leerChat(groupId, id);
      if (turno !== pedidoRef.current || actualRef.current !== id) return;
      setMensajes(Array.isArray(chat && chat.mensajes) ? chat.mensajes : []);
    } catch (e) {
      if (turno === pedidoRef.current) setError(errorDeAccion(e));
    }
  }, [groupId]);

  // Al abrir: la lista y el chat mas reciente (o el que ya estaba abierto, si sigue ahi).
  useEffect(() => {
    if (!abierto) return undefined;
    let vivo = true;
    setError(null);
    setConfirmando(false);
    listarChats(groupId)
      .then((lista) => {
        if (!vivo) return;
        const ordenados = ordenarChats(lista);
        setChats(ordenados);
        const elegido = ordenados.find((c) => c.id === actualRef.current) || ordenados[0] || null;
        if (elegido) {
          elegir(elegido.id);
          cargarChat(elegido.id);
        } else {
          elegir(null);
          setMensajes([]);
        }
      })
      .catch((e) => {
        if (!vivo) return;
        // pasada 4 (regla 7): la lista no se pudo leer; no es "Sin chats todavia" (el selector se queda apagado)
        setChats(null);
        setError(errorDeAccion(e));
      });
    return () => { vivo = false; };
  }, [abierto, groupId, cargarChat]);

  // El foco: a la caja al abrir; de vuelta a quien lo abrio al cerrar.
  useEffect(() => {
    if (abierto && cajaRef.current) cajaRef.current.focus();
    if (estabaAbierto.current && !abierto) {
      const el = volverA && volverA.current;
      if (el && el.isConnected && typeof el.focus === 'function') el.focus();
    }
    estabaAbierto.current = abierto;
  }, [abierto, volverA]);

  // Escape cierra el cajon; con la ventana chica abierta, solo la ventana chica.
  useEffect(() => {
    if (!abierto) return undefined;
    const alTeclear = (e) => {
      // t7-3 remate 8 (C28, C91 y C131): el foco no se sale del cajon ni de su ventana chica
      if (e.key === 'Tab') {
        const raiz = confirmando ? confirmarRef.current : cajonRef.current;
        const lista = enfocables(raiz);
        if (!lista.length) return;
        const primero = lista[0];
        const ultimo = lista[lista.length - 1];
        const activo = document.activeElement;
        const fuera = !raiz || !raiz.contains(activo);
        if (e.shiftKey && (fuera || activo === primero)) {
          e.preventDefault();
          ultimo.focus();
        } else if (!e.shiftKey && (fuera || activo === ultimo)) {
          e.preventDefault();
          primero.focus();
        }
        return;
      }
      if (e.key !== 'Escape') return;
      // post-t7-2b (C3, #607): con la lista de chats abierta, Esc cierra SOLO la lista y el foco vuelve a su boton
      if (listaAbiertaRef.current && !confirmando) {
        e.preventDefault();
        setListaAbierta(false);
        if (selectorRef.current) selectorRef.current.focus();
        return;
      }
      if (confirmando) { volverAlBoteRef.current = true; setConfirmando(false); } else if (alCerrar) alCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [abierto, alCerrar, confirmando]);

  // La ventana chica nace con el foco en "Cancelar" (lo que no destruye nada). t7-3 remate 8 (revisor de flujos, F1):
  // si se cierra SIN borrar (Cancelar o Esc), el foco vuelve al bote que la abrio (antes caia al fondo de la pagina).
  useEffect(() => {
    if (confirmando && cancelarRef.current) cancelarRef.current.focus();
    if (!confirmando && volverAlBoteRef.current) {
      volverAlBoteRef.current = false;
      if (boteRef.current && boteRef.current.isConnected) boteRef.current.focus();
    }
  }, [confirmando]);
  const cancelarBorrar = () => { volverAlBoteRef.current = true; setConfirmando(false); };
  // t7-3 remate 8 (revisor de flujos, segunda pasada, F2): el bote se vuelve a encender al terminar un borrado fallido;
  // ahi recibe el foco (el efecto de arriba no lo alcanza: mientras `ocupado` el bote sigue apagado)
  useEffect(() => {
    if (ocupado || confirmando || !volverAlBoteRef.current) return;
    if (boteRef.current && boteRef.current.isConnected && !boteRef.current.disabled) {
      volverAlBoteRef.current = false;
      boteRef.current.focus();
    }
  });

  // t7-3 remate 8 (C34): ATRAS cierra el cajon en vez de salir del panel. Al abrir se apila una entrada con la MISMA
  // direccion; si el profe va atras, esa entrada se consume y el cajon se cierra (lo escrito se queda: el cajon no se
  // desmonta). Si se cierra por otro camino, la entrada se retira para que ATRAS siga llevando a donde llevaba.
  useEffect(() => {
    if (!abierto || typeof window === 'undefined' || !window.history || !window.history.pushState) return undefined;
    let porAtras = false;
    try {
      window.history.pushState({ ...(window.history.state || {}), chatDelPanel: true }, '');
    } catch (x) { return undefined; }
    const alVolver = () => {
      porAtras = true;
      if (alCerrar) alCerrar();
    };
    window.addEventListener('popstate', alVolver);
    return () => {
      window.removeEventListener('popstate', alVolver);
      if (!porAtras && window.history.state && window.history.state.chatDelPanel) {
        try { window.history.back(); } catch (x) { /* nada que retirar */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  // t7-3 remate 8 (C98): la caja crece hasta 4 renglones y despues se desliza por dentro
  useEffect(() => {
    const el = cajaRef.current;
    if (!abierto || !el) return;
    el.style.height = `${ALTO_DE_LA_CAJA}px`;
    const { alto, desliza } = altoDeLaCaja(el.scrollHeight);
    el.style.height = `${alto}px`;
    el.style.overflowY = desliza ? 'auto' : 'hidden';
  }, [texto, abierto]);

  // t7-3 remate 8: el foco despues de pintar (abrir las sugerencias, elegir una, quitar un adjunto)
  useEffect(() => {
    const destino = enfocarRef.current;
    if (!destino) return;
    enfocarRef.current = null;
    if (destino === 'chip') {
      const primero = chipsRef.current && chipsRef.current.querySelector('[data-chip="si"]');
      if (primero) primero.focus();
    } else if (destino === 'plegar') {
      if (plegarRef.current) plegarRef.current.focus();
    } else if (destino === 'selector' || destino === 'nuevo') {
      const el = destino === 'selector' ? selectorRef.current : nuevoRef.current;
      if (el && !el.disabled) el.focus();
      else enfocarRef.current = destino;
    } else if (destino && destino.quitar !== undefined) {
      const botones = archivosRef.current ? archivosRef.current.querySelectorAll('[data-quitar="si"]') : [];
      const siguiente = botones[Math.min(destino.quitar, botones.length - 1)];
      if (siguiente) siguiente.focus();
      else if (cajaRef.current) cajaRef.current.focus();
    }
  });

  // La conversacion se queda abajo del todo.
  useEffect(() => {
    const el = listaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [mensajes, pendiente, actual]);

  // post-t7-2b (C3, #607): la lista de chats. Al abrir, el foco va al chat abierto (o al primero); un clic fuera de su
  // caja (el boton y la lista) la cierra; con el cajon cerrado, la lista tambien.
  useEffect(() => {
    if (!listaAbierta) return undefined;
    const ul = listaDeChatsRef.current;
    const destino = ul && (ul.querySelector('[aria-selected="true"]') || ul.querySelector('[role="option"]'));
    if (destino) destino.focus();
    const alTocar = (e) => {
      const caja = cajaDeLaListaRef.current;
      if (caja && !caja.contains(e.target)) setListaAbierta(false);
    };
    document.addEventListener('click', alTocar);
    return () => document.removeEventListener('click', alTocar);
  }, [listaAbierta]);
  useEffect(() => {
    if (!abierto) setListaAbierta(false);
  }, [abierto]);

  if (!abierto) return null;

  /* -- acciones ------------------------------------------------------------------ */

  const agregarALaLista = (chat) => {
    setChats((cs) => ordenarChats([
      { id: chat.id, titulo: chat.titulo || '', actualizado: chat.actualizado, mensajes_n: 0 },
      ...(cs || []).filter((c) => c.id !== chat.id),
    ]));
  };

  const nuevoChat = async () => {
    if (ocupado || pendiente) return;
    setOcupado(true);
    setError(null);
    try {
      const chat = await crearChat(groupId);
      agregarALaLista(chat);
      elegir(chat.id);
      pedidoRef.current += 1;
      setMensajes(Array.isArray(chat.mensajes) ? chat.mensajes : []);
      setSugerenciasAbiertas(false);
      if (cajaRef.current) cajaRef.current.focus();
    } catch (e) {
      setError(errorDeAccion(e));
    } finally {
      setOcupado(false);
    }
  };

  const cambiarDeChat = (id) => {
    if (!id || id === actualRef.current) return;
    setError(null);
    elegir(id);
    setMensajes([]);
    cargarChat(id);
  };

  const enviar = async (deUnChip) => {
    const pregunta = String(deUnChip !== undefined ? deUnChip : texto).trim();
    if (!pregunta || pendiente) return;
    const problema = revisarAdjuntos(archivos);
    if (problema) {
      setError(llaveDelCliente(problema));
      return;
    }
    const conEstos = archivos;
    setError(null);
    setPendiente({ texto: pregunta, adjuntos: conEstos.map((f) => ({ nombre: f.name })) });
    // Revisor t7-2b: un chip manda SU pregunta; lo que el profe ya habia escrito en la caja se
    // queda (antes se vaciaba tambien y se perdia sin aviso).
    if (deUnChip === undefined) setTexto('');
    setArchivos([]);
    // t7-3 remate 8 (revisor de flujos, segunda pasada, F1): "Enviar" se apaga mientras va; si el foco estaba en el,
    // pasa a la caja (antes caia al fondo de la pagina). Con Enter ya estaba en la caja.
    if (deUnChip === undefined && cajaRef.current && document.activeElement !== cajaRef.current) cajaRef.current.focus();
    try {
      let chatId = actualRef.current;
      if (!chatId) {
        const chat = await crearChat(groupId);
        agregarALaLista(chat);
        elegir(chat.id);
        pedidoRef.current += 1;
        setMensajes([]);
        chatId = chat.id;
      }
      const r = (await mandarPregunta(groupId, chatId, pregunta, conEstos, lang)) || {};
      if (actualRef.current === chatId) setMensajes((ms) => [...ms, r.profe, r.panel].filter(Boolean));
      if (r.chat) setChats((cs) => ordenarChats((cs || []).map((c) => (c.id === r.chat.id ? { ...c, ...r.chat } : c))));
    } catch (e) {
      setError(errorDelServidor(e));
      // El servidor no guardo nada: la pregunta y sus archivos vuelven a la caja.
      setTexto((ahora) => ahora || pregunta);
      setArchivos((ahora) => (ahora.length ? ahora : conEstos));
    } finally {
      setPendiente(null);
    }
  };

  const alTeclearLaCaja = (e) => {
    const componiendo = e.isComposing || (e.nativeEvent && e.nativeEvent.isComposing);
    if (e.key === 'Enter' && !e.shiftKey && !componiendo) {
      e.preventDefault();
      enviar();
    }
  };

  const alElegirArchivos = (e) => {
    const nuevos = Array.from((e.target && e.target.files) || []);
    const { lista, error: problema } = agregarAdjuntos(archivos, nuevos);
    setArchivos(lista);
    setError(problema ? llaveDelCliente(problema) : null);
    try { e.target.value = ''; } catch (x) { /* elegir el mismo archivo otra vez */ }
  };

  const quitar = (i) => {
    // t7-3 remate 8 (C28): el foco va al siguiente adjunto (o a la caja si era el ultimo)
    enfocarRef.current = { quitar: i };
    setArchivos((as) => as.filter((_, k) => k !== i));
  };

  const borrar = async () => {
    const id = actualRef.current;
    setConfirmando(false);
    if (!id) return;
    setOcupado(true);
    setError(null);
    try {
      try {
        await borrarChat(groupId, id);
      } catch (e1) {
        // t7-3 remate 7 (pasada 3, regla 7): si otra pestana ya lo borro (404 chat_no_encontrado), lo que el profe
        // pidio ya es cierto: se quita de la lista como un borrado, sin aviso
        const detalle = e1 && e1.response && e1.response.data ? e1.response.data.detail : null;
        if (!(e1 && e1.response && e1.response.status === 404 && detalle && detalle.error === 'chat_no_encontrado')) throw e1;
      }
      const resto = (chats || []).filter((c) => c.id !== id);
      setChats(resto);
      setMensajes([]);
      if (resto[0]) {
        elegir(resto[0].id);
        await cargarChat(resto[0].id);
      } else {
        elegir(null);
      }
      // t7-3 remate 8 (C91): el foco no se queda en un boton que ya no existe; va despues de pintar ("Nuevo chat"
      // esta apagado mientras dura el borrado)
      enfocarRef.current = resto[0] ? 'selector' : 'nuevo';
    } catch (e2) {
      setError(errorDeAccion(e2));
      // t7-3 remate 8 (revisor de flujos, segunda pasada, F2): no se borro; el foco vuelve al bote (se enciende al
      // terminar), como al cancelar
      volverAlBoteRef.current = true;
    } finally {
      setOcupado(false);
    }
  };

  /* -- lo que se pinta -------------------------------------------------------------- */

  const idTitulo = `chat-titulo-${groupId}`;
  const idConfirmar = `chat-confirmar-${groupId}`;
  const sinChats = Array.isArray(chats) && chats.length === 0;
  const adjuntar = t(`${L}.adjuntar`);
  const placeholder = t(`${L}.placeholder`);
  const enviarTexto = t(`${L}.enviar`);
  // t7-3 remate 8 (#590): las sugerencias salen de una funcion pura, con el avance que el panel ya tiene (hasta
  // cuatro). LAPIDA: aqui vivian cinco chips fijos, con "Quien no ha practicado esta semana?" (murio su llave).
  const chips = sugerenciasDelCajon(avance).map((s) => ({ id: s.id, texto: t(`${L}.${s.llave}`, s.params) }));
  const hayConversacion = mensajes.length > 0 || !!pendiente;
  const plegadas = hayConversacion && !sugerenciasAbiertas;
  const idSugerencias = `chat-sugerencias-${groupId}`;
  const elegirSugerencia = (texto) => {
    setSugerenciasAbiertas(false);
    enfocarRef.current = 'plegar';
    enviar(texto);
  };
  const alternarSugerencias = () => {
    if (!sugerenciasAbiertas) enfocarRef.current = 'chip';
    setSugerenciasAbiertas(!sugerenciasAbiertas);
  };
  const pregunta = (i) => {
    for (let k = i - 1; k >= 0; k -= 1) if (mensajes[k] && mensajes[k].rol === 'profe') return mensajes[k];
    return null;
  };

  // post-t7-2b (C3, #607): la lista de chats
  const listaApagada = !chats || sinChats || !!pendiente;
  const listaVisible = listaAbierta && !listaApagada;
  const idLista = `chat-lista-${groupId}`;
  const elegido = actual ? (chats || []).find((c) => c.id === actual) || null : null;
  const delElegido = elegido ? partesDelChat(t, elegido, lang) : null;
  const letraTactil = (base) => (tactil ? 16 : base);
  const cerrarLista = () => {
    setListaAbierta(false);
    if (selectorRef.current) selectorRef.current.focus();
  };
  const elegirDeLaLista = (id) => {
    cerrarLista();
    cambiarDeChat(id);
  };
  const alTeclearRenglon = (e, i, id) => {
    const renglones = listaDeChatsRef.current ? listaDeChatsRef.current.querySelectorAll('[role="option"]') : [];
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const k = e.key === 'ArrowDown' ? Math.min(i + 1, renglones.length - 1) : Math.max(i - 1, 0);
      if (renglones[k]) renglones[k].focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      elegirDeLaLista(id);
    }
  };

  const cajon = telefono
    ? { left: 0, right: 0, width: '100%' }
    : { right: 0, width: ANCHO_DEL_CAJON, maxWidth: '100%', borderLeft: '1px solid #2a2a2a', boxShadow: '-16px 0 40px rgba(0, 0, 0, 0.5)' };

  return createPortal(
    <>
      <div data-testid="chat-capa" aria-hidden="true" onClick={alCerrar}
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 70, backgroundColor: 'rgba(0, 0, 0, 0.6)' }} />
      <div ref={cajonRef} role="dialog" aria-modal="true" aria-labelledby={idTitulo} data-testid="chat-cajon"
        data-telefono={telefono ? 'si' : 'no'}
        className={`flex flex-col ${telefono ? 'pl-[env(safe-area-inset-left,0px)] ' : ''}pr-[env(safe-area-inset-right,0px)]`}
        style={{ position: 'fixed', top: 0, bottom: 0, ...cajon, zIndex: 71, backgroundColor: '#121212', color: '#f0f0f0', overflowY: 'auto' }}>

        {/* la cabecera */}
        <div data-testid="chat-cabecera" className="pt-[calc(1rem+env(safe-area-inset-top,0px))]"
          style={{ paddingLeft: 20, paddingRight: 20, paddingBottom: 12, flexShrink: 0, borderBottom: '1px solid #1f1f1f' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MessageCircle className="w-5 h-5 shrink-0 text-[#FF6B00]" aria-hidden="true" />
            <h2 id={idTitulo} style={{ fontSize: 17, fontWeight: 700, color: '#ffffff', flex: 1, minWidth: 0 }}>
              {t('groups.avance.preguntale')}
            </h2>
            <button type="button" onClick={alCerrar} aria-label={t('common:close')} data-testid="chat-cerrar"
              className="text-[#a0a0a0] hover:text-white hover:bg-[#1f1f1f] transition-colors"
              style={{
                position: 'relative', height: ALTO_DEL_DIBUJO, paddingLeft: 10, paddingRight: 10, borderRadius: 8, fontSize: 13, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', gap: 4, borderWidth: 1, borderStyle: 'solid', borderColor: '#333333',
              }}>
              <AreaTocable borde={1} />
              <X className="w-4 h-4" aria-hidden="true" />
              {t('common:close')}
            </button>
          </div>
          <p data-testid="chat-explicacion" style={{ fontSize: 12, color: GRIS, marginTop: 6, lineHeight: 1.45 }}>
            {t(`${L}.explicacion`, { grupo: grupoNombre, group: grupoNombre, hora, time: hora })}
          </p>

          {/* el renglon: nuevo chat, tus chats, borrar */}
          {/* post-t7-2b (C3, #607): el renglon es la referencia de la lista de chats (va a todo su ancho) y sus piezas se
              alinean arriba: el nombre del chat abierto puede ocupar dos renglones */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12 }}>
            <button ref={nuevoRef} type="button" onClick={nuevoChat} disabled={ocupado || !!pendiente} data-testid="chat-nuevo"
              className={`${BOTON_NARANJA} transition-colors`}
              style={{
                position: 'relative', height: ALTO_DEL_DIBUJO, paddingLeft: 12, paddingRight: 12, borderRadius: 8, fontSize: 13, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, whiteSpace: 'nowrap',
              }}>
              <AreaTocable />
              <Plus className="w-3.5 h-3.5" aria-hidden="true" />
              {t(`${L}.nuevo`)}
            </button>
            {/* post-t7-2b (C3, #607): el boton y su lista (antes un selector del sistema). La caja no tiene posicion: la
                lista se mide contra el renglon. LAPIDA: la etiqueta de 44 de toque del selector (remate 7, regla 2). */}
            <div ref={cajaDeLaListaRef} style={{ flex: 1, minWidth: 0 }}>
              <button ref={selectorRef} type="button" data-testid="chat-selector" aria-label={t(`${L}.tus_chats`)}
                aria-haspopup="listbox" aria-expanded={listaVisible ? 'true' : 'false'} aria-controls={idLista}
                value={actual || ''} disabled={listaApagada}
                onClick={() => setListaAbierta(!listaAbierta)}
                className="disabled:opacity-40"
                style={{
                  position: 'relative', width: '100%', minHeight: ALTO_DEL_DIBUJO, display: 'flex', alignItems: 'center',
                  gap: 8, textAlign: 'left', backgroundColor: '#1a1a1a', borderWidth: 1, borderStyle: 'solid',
                  borderColor: listaVisible ? NARANJA : '#333333', borderRadius: 8, color: '#e5e5e5', paddingTop: 6,
                  paddingBottom: 6, paddingLeft: 10, paddingRight: 8, fontSize: letraTactil(13), lineHeight: 1.35,
                }}>
                <AreaTocable borde={1} />
                <span data-testid="chat-selector-texto"
                  style={{ flex: 1, minWidth: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {delElegido ? (
                    <>
                      {delElegido.cuando ? <>{delElegido.cuando}{': '}</> : null}
                      {delElegido.titulo}
                    </>
                  ) : t(sinChats ? `${L}.sin_chats` : `${L}.tus_chats`)}
                </span>
                <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${listaVisible ? 'rotate-180' : ''}`}
                  aria-hidden="true" style={{ color: GRIS }} />
              </button>
              <ul ref={listaDeChatsRef} id={idLista} role="listbox" aria-label={t(`${L}.tus_chats`)} data-testid="chat-lista"
                hidden={!listaVisible}
                style={{
                  position: 'absolute', left: 0, right: 0, top: 'calc(100% + 6px)', zIndex: 5, backgroundColor: '#181818',
                  borderWidth: 1, borderStyle: 'solid', borderColor: '#333333', borderRadius: 12, padding: 6, margin: 0,
                  maxHeight: 316, overflowY: 'auto', boxShadow: '0 18px 40px -12px rgba(0, 0, 0, 0.8)',
                  display: listaVisible ? 'grid' : 'none', gap: 2, listStyle: 'none',
                }}>
                {(chats || []).map((c, i) => {
                  const { cuando, titulo } = partesDelChat(t, c, lang);
                  const esEste = c.id === actual;
                  const fondo = esEste ? ACENTO_SUAVE : (resaltado === c.id ? FONDO_RESALTADO : 'transparent');
                  return (
                    <li key={c.id} role="option" tabIndex={-1} aria-selected={esEste ? 'true' : 'false'}
                      data-testid={`chat-opcion-${c.id}`} data-chat-id={c.id}
                      onClick={() => elegirDeLaLista(c.id)} onKeyDown={(e) => alTeclearRenglon(e, i, c.id)}
                      onMouseEnter={() => setResaltado(c.id)} onMouseLeave={() => setResaltado(null)}
                      onFocus={() => setResaltado(c.id)} onBlur={() => setResaltado(null)}
                      style={{
                        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', rowGap: 2, columnGap: 10,
                        alignItems: 'center', minHeight: ALTO_TOCABLE, paddingTop: 8, paddingBottom: 8, paddingLeft: 10,
                        paddingRight: 10, borderRadius: 8, cursor: 'pointer', color: '#e5e5e5', backgroundColor: fondo,
                        outline: 'none',
                      }}>
                      {cuando ? (
                        <span data-testid="chat-opcion-fecha" style={{ gridColumn: 1, fontSize: 11, color: GRIS, lineHeight: 1.2 }}>
                          {cuando}
                        </span>
                      ) : null}
                      <span data-testid="chat-opcion-titulo"
                        style={{
                          gridColumn: 1, fontSize: letraTactil(13.5), lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical', overflow: 'hidden', color: esEste ? NARANJA_CLARO : undefined,
                        }}>
                        {titulo}
                      </span>
                      <span data-testid="chat-opcion-palomita" aria-hidden="true"
                        style={{ gridColumn: 2, gridRow: '1 / span 2', color: NARANJA, visibility: esEste ? 'visible' : 'hidden', display: 'inline-flex' }}>
                        <Check className="w-4 h-4" aria-hidden="true" />
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
            {actual && (
              <button type="button" ref={boteRef} onClick={() => setConfirmando(true)} disabled={ocupado || !!pendiente}
                aria-label={t(`${L}.borrar`)} title={t(`${L}.borrar`)} data-testid="chat-borrar"
                className="text-[#a0a0a0] hover:text-white hover:bg-[#1f1f1f] transition-colors disabled:opacity-40"
                style={{
                  position: 'relative', width: ALTO_DEL_DIBUJO, height: ALTO_DEL_DIBUJO, borderRadius: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', borderWidth: 1, borderStyle: 'solid', borderColor: '#333333',
                }}>
                <AreaTocable borde={1} deLado />
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* las preguntas sugeridas. t7-2c (B8): con el quinto chip ya no caben en un renglon a
              375, asi que la fila PARTE renglon en vez de deslizarse de lado. Entre renglones van
              12 px: los 6 de la extension de 44 de cada chip (R3a) hacia arriba y hacia abajo, sin
              que dos extensiones se encimen. El relleno de arriba y el margen de abajo son los de
              antes: los chips quedan a 12 del renglon y la cabecera termina donde terminaba. */}
          {/* t7-3 remate 8 (#590): sin alumnos o sin publicaciones no hay sugerencias, ni su bloque. Con conversacion,
              el primer elemento de la fila es "Sugerencias" (el mismo dibujo que un chip, con su flecha) y las
              sugerencias van detras, plegadas hasta que se abre. */}
          {chips.length > 0 && (
          <div ref={chipsRef} id={idSugerencias} data-testid="chat-chips" data-plegadas={plegadas ? 'si' : 'no'}
            style={{
              display: 'flex', flexWrap: 'wrap', columnGap: 8, rowGap: 2 * EXTRA_TOCABLE, marginTop: 12 - EXTRA_TOCABLE,
              marginLeft: -20, marginRight: -20, paddingLeft: 20, paddingRight: 20,
              paddingTop: EXTRA_TOCABLE, paddingBottom: EXTRA_TOCABLE, marginBottom: -EXTRA_TOCABLE,
            }}>
            {hayConversacion && (
              <button ref={plegarRef} type="button" data-testid="chat-sugerencias" onClick={alternarSugerencias}
                aria-expanded={!plegadas} aria-controls={idSugerencias}
                className="text-[#e5e5e5] hover:bg-[#1a1a1a] hover:text-white transition-colors"
                style={{ ...ESTILO_DEL_CHIP, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <AreaTocable borde={1} />
                {t(`${L}.sugerencias`)}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${plegadas ? '' : 'rotate-180'}`} aria-hidden="true" />
              </button>
            )}
            {!plegadas && chips.map((c) => (
              <button key={c.id} type="button" data-testid={`chat-chip-${c.id}`} data-chip="si"
                onClick={() => elegirSugerencia(c.texto)} disabled={!!pendiente}
                className="text-[#e5e5e5] hover:bg-[#1a1a1a] hover:text-white transition-colors disabled:opacity-40"
                // t7-3 remate 7 (regla 2 de #581): un chip con un nombre larguisimo parte renglon dentro del cajon (antes
                // se salia de lado y el cajon entero se deslizaba); con un renglon mide los mismos 32
                style={ESTILO_DEL_CHIP}>
                <AreaTocable borde={1} />
                {c.texto}
              </button>
            ))}
          </div>
          )}
        </div>

        {/* la conversacion */}
        <div ref={listaRef} data-testid="chat-mensajes"
          style={{
            flex: 1, minHeight: ALTO_MINIMO_DE_LA_CONVERSACION, overflowY: 'auto', paddingTop: 16, paddingBottom: 16, paddingLeft: 20, paddingRight: 20,
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
          {mensajes.map((m, i) => (m && m.rol === 'profe' ? (
            <MensajeDelProfe key={`${i}-${m.id}`} t={t} mensaje={m} testid={`chat-mensaje-${m.id}`} />
          ) : m ? (
            <MensajeDelPanel key={`${i}-${m.id}`} t={t} mensaje={m} pregunta={pregunta(i)} testid={`chat-mensaje-${m.id}`} />
          ) : null))}
          {pendiente && <MensajeDelProfe t={t} mensaje={pendiente} testid="chat-pregunta-en-vuelo" />}
          {pendiente && <Pensando t={t} />}
        </div>

        {/* el pie: el aviso, los adjuntos, la caja, adjuntar y la nota */}
        <div data-testid="chat-pie" className="pb-[calc(1rem+env(safe-area-inset-bottom,0px))]"
          style={{ paddingTop: 12, paddingLeft: 20, paddingRight: 20, flexShrink: 0, borderTop: '1px solid #1f1f1f' }}>
          {error && (
            <p data-testid="chat-error" role="alert" style={{ fontSize: 13, color: '#ff8a80', marginBottom: 8 }}>
              {t(error.llave, error.params)}
            </p>
          )}
          {archivos.length > 0 && (
            <ul ref={archivosRef} data-testid="chat-archivos" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {archivos.map((f, i) => (
                <li key={`${f.name}-${i}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', minWidth: 0, height: 28,
                    paddingLeft: 10, paddingRight: 2, borderRadius: 999, borderWidth: 1, borderStyle: 'solid',
                    borderColor: '#333333', backgroundColor: '#1a1a1a', fontSize: 12, color: '#d8d8d8',
                  }}>
                  <Paperclip className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                  <span data-testid={`chat-archivo-nombre-${i}`} className="truncate">{f.name}</span>
                  <button type="button" onClick={() => quitar(i)} data-testid={`chat-quitar-${i}`} data-quitar="si"
                    aria-label={t(`${L}.quitar_archivo`, { nombre: f.name, name: f.name })}
                    className="text-[#a0a0a0] hover:text-white transition-colors"
                    style={{ width: 24, height: 24, borderRadius: 999, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      position: 'relative' }}>
                    {/* t7-3 remate 7 (regla 2 de #581): el area de toque de 44 sin cambiar el dibujo de 24 */}
                    <span aria-hidden="true" data-area-tocable="si" style={{ position: 'absolute', top: -10, bottom: -10, left: -10, right: -10 }} />
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <textarea ref={cajaRef} data-testid="chat-caja" rows={1} maxLength={TOPE_PREGUNTA}
              value={texto} placeholder={placeholder} aria-label={placeholder}
              onChange={(e) => setTexto(e.target.value)} onKeyDown={alTeclearLaCaja}
              className="outline-none focus:border-[#FF6B00]"
              style={{
                flex: 1, minWidth: 0, height: 44, resize: 'none', boxSizing: 'border-box', borderRadius: 10,
                backgroundColor: '#1a1a1a', borderWidth: 1, borderStyle: 'solid', borderColor: '#333333',
                color: '#f0f0f0', fontSize: 14, lineHeight: '20px', paddingTop: 11, paddingBottom: 11,
                paddingLeft: 12, paddingRight: 12,
              }} />
            <button type="button" onClick={() => enviar()} disabled={!texto.trim() || !!pendiente}
              aria-label={enviarTexto} data-testid="chat-enviar"
              className={`${BOTON_NARANJA} transition-colors`}
              style={{
                height: 44, paddingLeft: 16, paddingRight: 16, borderRadius: 10, fontSize: 14, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
              }}>
              <Send className="w-4 h-4" aria-hidden="true" />
              {enviarTexto}
            </button>
          </div>
          <button type="button" onClick={() => archivoRef.current && archivoRef.current.click()}
            disabled={!!pendiente} aria-label={adjuntar} data-testid="chat-adjuntar"
            className="text-[#a0a0a0] hover:text-white transition-colors disabled:opacity-40"
            style={{ marginTop: 8, minHeight: 32, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, textAlign: 'left', maxWidth: '100%',
              position: 'relative' }}>
            {/* t7-3 remate 7 (regla 2 de #581): el area de toque de 44 (su dibujo mide 32) */}
            <AreaTocable />
            <Paperclip className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span>{adjuntar}</span>
          </button>
          <input ref={archivoRef} data-testid="chat-archivo-input" type="file" multiple accept={ACEPTA}
            onChange={alElegirArchivos} tabIndex={-1} aria-hidden="true" style={{ display: 'none' }} />
          {/* t7-3 remate 7 (regla 2 de #581): la nota en el gris legible (antes #6a6a6a, 3.5 a 1) */}
          <p data-testid="chat-nota" style={{ fontSize: 12, color: GRIS, marginTop: 4 }}>{t(`${L}.nota`)}</p>
        </div>

        {/* la ventana chica de borrar */}
        {confirmando && (
          <div data-testid="chat-confirmar-capa"
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 80, backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}>
            <div ref={confirmarRef} role="alertdialog" aria-modal="true" aria-labelledby={idConfirmar} data-testid="chat-confirmar-borrar"
              className={VENTANA_PANEL}>
              <h3 id={idConfirmar} className={VENTANA_TITULO}>{t(`${L}.borrar_confirmar`)}</h3>
              <div className={BOTONERA}>
                <Button ref={cancelarRef} variant="outline" onClick={cancelarBorrar}
                  className={BOTON_CANCELAR} data-testid="chat-confirmar-no">
                  {t('common:cancel')}
                </Button>
                <Button onClick={borrar} className={BOTON_GUARDAR} data-testid="chat-confirmar-si">
                  {t(`${L}.borrar`)}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
